import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Fab } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useMobile } from '../hooks/useMobile';
import {
  useProjectsTree,
  useDeleteProject,
  buildTree,
  flattenTree,
  collectSubtreeIds,
} from '../hooks/useProjects';
import ProjectTree from '../components/projects/ProjectTree';
import ProjectDetail from '../components/projects/ProjectDetail';
import ProjectFormDialog from '../components/projects/ProjectFormDialog';
import MoveProjectDialog from '../components/projects/MoveProjectDialog';
import LogHoursDialog from '../components/projects/LogHoursDialog';
import { TAB_HEIGHT } from '../components/layout/BottomTabBar';

export default function ProjectsPage() {
  const { isMobile } = useMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || null;

  const { data: flat = [], isLoading } = useProjectsTree();
  const deleteMut = useDeleteProject();

  const tree = useMemo(() => buildTree(flat), [flat]);
  const flatWithDepth = useMemo(() => flattenTree(tree), [tree]);
  const flatById = useMemo(() => {
    const m = new Map();
    flat.forEach((p) => m.set(p.id, p));
    return m;
  }, [flat]);

  // Expanded state — default: all nodes with children expanded
  const [expanded, setExpanded] = useState(() => new Set());
  useEffect(() => {
    // When tree first loads, expand every node that has children
    if (flat.length && expanded.size === 0) {
      const parentIds = new Set();
      flat.forEach((p) => { if (p.parent_id) parentIds.add(p.parent_id); });
      // Always include roots even if childless, so the chevron state is consistent
      tree.forEach((n) => parentIds.add(n.id));
      setExpanded(parentIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat.length]);

  const expandAll = useCallback(() => {
    const ids = new Set();
    flat.forEach((p) => { if (p.parent_id) ids.add(p.parent_id); });
    tree.forEach((n) => ids.add(n.id));
    setExpanded(ids);
  }, [flat, tree]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const setSelectedId = useCallback((id) => {
    const params = new URLSearchParams(searchParams);
    if (id) params.set('id', id);
    else params.delete('id');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Dialogs
  const [formDialog, setFormDialog] = useState(null); // null | { mode: 'create', parentId? } | { mode: 'edit', project }
  const [moveDialog, setMoveDialog] = useState(null); // null | project
  const [confirmDelete, setConfirmDelete] = useState(null); // null | project
  const [globalLogOpen, setGlobalLogOpen] = useState(false);

  const onAction = useCallback((id, action) => {
    const project = flatById.get(id);
    if (!project) return;
    if (action === 'rename') setFormDialog({ mode: 'edit', project });
    else if (action === 'move') setMoveDialog(project);
    else if (action === 'addChild') {
      setFormDialog({ mode: 'create', parentId: id });
      setExpanded((prev) => { const n = new Set(prev); n.add(id); return n; });
    }
    else if (action === 'delete') setConfirmDelete(project);
  }, [flatById]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    await deleteMut.mutateAsync(confirmDelete.id);
    if (selectedId === confirmDelete.id) setSelectedId(null);
    setConfirmDelete(null);
  };

  // Compute disabled ids for Move dialog (self + descendants)
  const moveDisabled = useMemo(() => {
    if (!moveDialog) return new Set();
    return collectSubtreeIds(tree, moveDialog.id);
  }, [moveDialog, tree]);

  // Mobile: if selected, show detail fullscreen; otherwise tree.
  const showTree = !isMobile || !selectedId;
  const showDetail = !isMobile || !!selectedId;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 80px)' }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Staycation</Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {showTree && (
          <Paper
            square
            variant="outlined"
            sx={{
              width: isMobile ? '100%' : 340,
              minWidth: isMobile ? 'auto' : 340,
              borderTop: 0, borderBottom: 0, borderLeft: 0,
              display: 'flex', flexDirection: 'column',
            }}
          >
            {isLoading ? (
              <Typography variant="body2" sx={{ p: 2 }}>Loading…</Typography>
            ) : (
              <ProjectTree
                tree={tree}
                selectedId={selectedId}
                expanded={expanded}
                onToggle={toggleExpand}
                onSelect={setSelectedId}
                onAction={onAction}
                onNewRoot={() => setFormDialog({ mode: 'create' })}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
              />
            )}
          </Paper>
        )}
        {showDetail && (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {isMobile && selectedId && (
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Button size="small" onClick={() => setSelectedId(null)}>‹ Back to tree</Button>
              </Box>
            )}
            <ProjectDetail
              projectId={selectedId}
              flatById={flatById}
              onEdit={(project) => setFormDialog({ mode: 'edit', project })}
            />
          </Box>
        )}
      </Box>

      {/* Dialogs */}
      <ProjectFormDialog
        open={!!formDialog}
        onClose={() => setFormDialog(null)}
        initial={formDialog?.mode === 'edit' ? formDialog.project : null}
        defaultParentId={formDialog?.mode === 'create' ? formDialog.parentId : null}
        flatProjects={flatWithDepth}
        onSaved={(p) => { if (p?.id && formDialog?.mode === 'create') setSelectedId(p.id); }}
      />

      <MoveProjectDialog
        open={!!moveDialog}
        onClose={() => setMoveDialog(null)}
        project={moveDialog}
        flatProjects={flatWithDepth}
        disabledIds={moveDisabled}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete project?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>"{confirmDelete?.name}"</strong> and all its sub-projects, tasks, notes, and files.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Linked time entries and expenses will be unlinked but not deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button onClick={doDelete} color="error" variant="contained" disabled={deleteMut.isPending}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Fab
        color="primary"
        onClick={() => setGlobalLogOpen(true)}
        sx={{ position: 'fixed', bottom: isMobile ? `calc(${TAB_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)` : 24, right: 24, zIndex: 1200 }}
        aria-label="Log hours"
      >
        <AccessTimeIcon />
      </Fab>
      <LogHoursDialog
        open={globalLogOpen}
        onClose={() => setGlobalLogOpen(false)}
        defaultProjectId={selectedId && flatById.get(selectedId)?.kind === 'segment' ? selectedId : undefined}
      />
    </Box>
  );
}
